export type EditMode = "edit" | "read";

export type NotifyKey = "page" | "mentionPage" | "mentionComment" | "comment" | "resolved";

const notifyDefault: Record<NotifyKey, boolean> = {
  page: true,
  mentionPage: true,
  mentionComment: true,
  comment: true,
  resolved: true,
};

export function readWide(): boolean {
  return localStorage.getItem("hiera.wide") === "1";
}

export function applyWide(on: boolean): void {
  document.documentElement.dataset.wide = on ? "1" : "0";
}

export function writeWide(on: boolean): void {
  localStorage.setItem("hiera.wide", on ? "1" : "0");
  applyWide(on);
}

export function readEditMode(): EditMode {
  return localStorage.getItem("hiera.editMode") === "read" ? "read" : "edit";
}

export function writeEditMode(mode: EditMode): void {
  localStorage.setItem("hiera.editMode", mode);
}

export function readToolbar(): boolean {
  return localStorage.getItem("hiera.toolbar") === "1";
}

export function writeToolbar(on: boolean): void {
  localStorage.setItem("hiera.toolbar", on ? "1" : "0");
}

export function readNotify(): Record<NotifyKey, boolean> {
  try {
    const raw = JSON.parse(localStorage.getItem("hiera.notify") || "{}") as Partial<Record<NotifyKey, boolean>>;
    return { ...notifyDefault, ...raw };
  } catch {
    return { ...notifyDefault };
  }
}

export type TreeSort = "order" | "name" | "updated";
export type SidePanelMode = "workspace" | "recent";
export type PageTint =
  | "rose"
  | "peach"
  | "lemon"
  | "mint"
  | "sky"
  | "lavender"
  | "blush";

export const PAGE_TINTS: PageTint[] = ["rose", "peach", "lemon", "mint", "sky", "lavender", "blush"];

const RECENT_KEY = "hiera.recentPages";
const RECENT_LIMIT = 24;
const PINS_KEY = "hiera.pagePins";
const TINTS_KEY = "hiera.pageTints";
const SIDE_MODE_KEY = "hiera.sideMode";
const FAV_FILTER_KEY = "hiera.treeFavOnly";

function readSpaceMap(key: string): Record<string, string[]> {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "{}") as Record<string, unknown>;
    const out: Record<string, string[]> = {};
    for (const [id, value] of Object.entries(raw)) {
      if (Array.isArray(value)) out[id] = value.filter((row): row is string => typeof row === "string");
    }
    return out;
  } catch {
    return {};
  }
}

function writeSpaceMap(key: string, all: Record<string, string[]>): void {
  localStorage.setItem(key, JSON.stringify(all));
}

export function readRecentPages(spaceId: string): string[] {
  return (readSpaceMap(RECENT_KEY)[spaceId] || []).slice(0, RECENT_LIMIT);
}

export function pushRecentPage(spaceId: string, pageId: string): string[] {
  if (!spaceId || !pageId) return readRecentPages(spaceId);
  const all = readSpaceMap(RECENT_KEY);
  const next = [pageId, ...(all[spaceId] || []).filter((id) => id !== pageId)].slice(0, RECENT_LIMIT);
  all[spaceId] = next;
  writeSpaceMap(RECENT_KEY, all);
  return next;
}

export function readPinnedPages(spaceId: string): string[] {
  return readSpaceMap(PINS_KEY)[spaceId] || [];
}

export function writePinnedPages(spaceId: string, ids: string[]): string[] {
  const all = readSpaceMap(PINS_KEY);
  const next = ids.filter((id, i, arr) => id && arr.indexOf(id) === i);
  if (next.length) all[spaceId] = next;
  else delete all[spaceId];
  writeSpaceMap(PINS_KEY, all);
  return next;
}

export function togglePinnedPage(spaceId: string, pageId: string): string[] {
  const cur = readPinnedPages(spaceId);
  const next = cur.includes(pageId) ? cur.filter((id) => id !== pageId) : [pageId, ...cur];
  return writePinnedPages(spaceId, next);
}

export function readPageTints(spaceId: string): Record<string, PageTint> {
  try {
    const raw = JSON.parse(localStorage.getItem(TINTS_KEY) || "{}") as Record<string, unknown>;
    const bag = raw[spaceId];
    if (!bag || typeof bag !== "object") return {};
    const out: Record<string, PageTint> = {};
    for (const [id, value] of Object.entries(bag as Record<string, unknown>)) {
      if (PAGE_TINTS.includes(value as PageTint)) out[id] = value as PageTint;
    }
    return out;
  } catch {
    return {};
  }
}

export function writePageTint(spaceId: string, pageId: string, tint: PageTint | null): Record<string, PageTint> {
  let all: Record<string, Record<string, PageTint>> = {};
  try {
    all = JSON.parse(localStorage.getItem(TINTS_KEY) || "{}") as Record<string, Record<string, PageTint>>;
  } catch {
    all = {};
  }
  const bag = { ...(all[spaceId] || {}) };
  if (tint) bag[pageId] = tint;
  else delete bag[pageId];
  if (Object.keys(bag).length) all[spaceId] = bag;
  else delete all[spaceId];
  localStorage.setItem(TINTS_KEY, JSON.stringify(all));
  return bag;
}

export function readSidePanelMode(spaceId: string): SidePanelMode {
  try {
    const raw = JSON.parse(localStorage.getItem(SIDE_MODE_KEY) || "{}") as Record<string, unknown>;
    return raw[spaceId] === "recent" ? "recent" : "workspace";
  } catch {
    return "workspace";
  }
}

export function writeSidePanelMode(spaceId: string, mode: SidePanelMode): void {
  let all: Record<string, SidePanelMode> = {};
  try {
    all = JSON.parse(localStorage.getItem(SIDE_MODE_KEY) || "{}") as Record<string, SidePanelMode>;
  } catch {
    all = {};
  }
  all[spaceId] = mode;
  localStorage.setItem(SIDE_MODE_KEY, JSON.stringify(all));
}

export function readTreeFavOnly(): boolean {
  return localStorage.getItem(FAV_FILTER_KEY) === "1";
}

export function writeTreeFavOnly(on: boolean): void {
  localStorage.setItem(FAV_FILTER_KEY, on ? "1" : "0");
}

export function readTreeSort(): TreeSort {
  const raw = localStorage.getItem("hiera.treeSort");
  if (raw === "name" || raw === "updated") return raw;
  return "order";
}

export function writeTreeSort(sort: TreeSort): void {
  localStorage.setItem("hiera.treeSort", sort);
}

const SIDEBAR_COLLAPSED_KEY = "hiera.sidebarCollapsed";

export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(on: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, on ? "1" : "0");
  } catch {
    /* private mode / quota */
  }
}

const TREE_CLOSED_KEY = "hiera.treeClosed";

/** `saved: false` — first visit (default: collapse folders). Empty `ids` = all open. */
export function readTreeClosed(spaceId: string): { saved: boolean; ids: string[] } {
  if (!spaceId) return { saved: false, ids: [] };
  try {
    const raw = JSON.parse(localStorage.getItem(TREE_CLOSED_KEY) || "{}") as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(raw, spaceId)) return { saved: false, ids: [] };
    const list = raw[spaceId];
    if (!Array.isArray(list)) return { saved: true, ids: [] };
    return {
      saved: true,
      ids: list.filter((id): id is string => typeof id === "string"),
    };
  } catch {
    return { saved: false, ids: [] };
  }
}

export function writeTreeClosed(spaceId: string, ids: string[]): void {
  if (!spaceId) return;
  try {
    const raw = JSON.parse(localStorage.getItem(TREE_CLOSED_KEY) || "{}") as Record<string, string[]>;
    raw[spaceId] = ids.filter((id, i, arr) => id && arr.indexOf(id) === i);
    localStorage.setItem(TREE_CLOSED_KEY, JSON.stringify(raw));
  } catch {
    /* private mode / quota */
  }
}

export function writeNotify(key: NotifyKey, on: boolean): Record<NotifyKey, boolean> {
  const next = { ...readNotify(), [key]: on };
  localStorage.setItem("hiera.notify", JSON.stringify(next));
  return next;
}
